import type { FieldValues, Path, Control } from 'react-hook-form'

import { Button } from 'src/shared/components/ui/buttons/button'
import {
  FormField,
  FormItem,
  FormLabel,
  FormControl,
  FormDescription,
  FormMessage,
} from 'src/shared/components/ui/forms/form'
import type { MapItem } from 'src/shared/schemas/utils'
import { cn } from 'src/shared/utils/cn'

// Improved interface with better typing for values
export interface ButtonSelectFormFieldProps<T extends FieldValues, V = string> {
  control?: Control<T>
  name: Path<T>
  label?: string
  options: Record<string, MapItem & { value?: V }>
  description?: string
  className?: string
  fieldClassName?: string
  buttonClassName?: string
  textClassName?: string
  unselectedButtonClassName?: string
  disabled?: boolean
  multiSelect?: boolean
  allowDeselect?: boolean
  gridCols?: number
  required?: boolean
  maxSelections?: number
  showCloseIcon?: boolean
  onValueChange?: (value: V | V[] | null) => void
  variant?: 'default' | 'list'
  labelClassName?: string
  iconClassName?: string
  showError?: boolean
  /** If true, the last row will be centered. */
  centerLastRow?: boolean
  // Removing parseBoolean in favor of using the generic V type
  hideIcon?: boolean
}

export function ButtonSelectFormField<T extends FieldValues, V = string>({
  control,
  name,
  label,
  options,
  description,
  className,
  fieldClassName,
  buttonClassName,
  textClassName,
  unselectedButtonClassName,
  disabled,
  multiSelect = false,
  allowDeselect = true,
  gridCols = 2,
  required = false,
  maxSelections,
  showCloseIcon = true,
  onValueChange,
  labelClassName,
  showError = true,
  variant = 'default',
  iconClassName,
  centerLastRow = true,
  hideIcon = false,
}: ButtonSelectFormFieldProps<T, V>) {
  // Used to calculate info for centering the last row.
  let effectiveGridCols = gridCols
  let lastRowShift = 0
  let lastRowFirstItemIndex = 0
  if (centerLastRow) {
    effectiveGridCols *= 2
    const totalItems = Object.keys(options).length
    const itemsInLastRow = totalItems % gridCols
    lastRowShift = gridCols - itemsInLastRow
    lastRowFirstItemIndex = totalItems - itemsInLastRow
  }

  return (
    <FormField
      control={control}
      name={name}
      render={({ field }) => (
        <FormItem className={cn('w-full', className)}>
          {label && <FormLabel className={cn(labelClassName)}>{required ? `${label} *` : label}</FormLabel>}
          {description && <FormDescription>{description}</FormDescription>}
          <FormControl>
            <div
              className={cn(
                variant === 'default'
                  ? {
                      'grid gap-2': true,
                      // 'grid-cols-1': gridCols === 1,
                      // 'grid-cols-2': gridCols === 2,
                      // 'grid-cols-3': gridCols === 3,
                      // 'grid-cols-4': gridCols === 4,
                      // 'grid-cols-5': gridCols === 5,
                    }
                  : 'flex flex-col gap-2',
                fieldClassName,
              )}
              style={{ gridTemplateColumns: `repeat(${effectiveGridCols}, minmax(0, 1fr))` }}
            >
              {Object.entries(options).map(([key, { label: buttonLabel, icon: Icon, value }], index) => {
                // Use the explicitly provided value or fall back to the key
                const optionValue = value !== undefined ? value : (key as unknown as V)

                // Simplified selection logic
                const isSelected = multiSelect
                  ? Array.isArray(field.value) && field.value.some((v: any) => v === optionValue)
                  : field.value === optionValue

                const atMaxSelections = Boolean(multiSelect && maxSelections && field.value?.length >= maxSelections)

                // Used to shift the last row to the right, so that the last row is centered.
                const shouldShift = centerLastRow && index === lastRowFirstItemIndex

                return (
                  <Button
                    key={key}
                    type="button"
                    variant={variant === 'list' ? 'multiSelect' : isSelected ? 'fadedSecondary' : 'accent'}
                    aria-selected={isSelected}
                    disabled={Boolean(disabled || (!isSelected && atMaxSelections))}
                    onClick={() => {
                      let newValue: V | V[] | null

                      if (multiSelect) {
                        if (isSelected) {
                          newValue = field.value?.filter((val: V) => val !== optionValue) as V[]
                        } else if (!maxSelections || field.value?.length < maxSelections) {
                          newValue = [...(field.value || []), optionValue] as V[]
                        } else {
                          return // Do nothing if at max selections
                        }
                      } else {
                        newValue = isSelected && allowDeselect ? null : optionValue
                      }

                      // Direct value assignment without conversion
                      field.onChange(newValue)
                      onValueChange?.(newValue)
                    }}
                    showCloseIcon={variant !== 'list' && isSelected && allowDeselect && showCloseIcon}
                    isSelected={isSelected}
                    className={cn(
                      'flex h-auto w-full',
                      variant === 'default' && 'min-h-16 flex-col items-center justify-center p-3',
                      centerLastRow && `col-span-2`,
                      variant === 'list' && 'items-center justify-start p-4',
                      buttonClassName,
                      unselectedButtonClassName && !isSelected && unselectedButtonClassName,
                    )}
                    style={{ gridColumnStart: shouldShift ? lastRowShift + 1 : undefined }}
                  >
                    <div
                      className={cn(
                        'flex items-center',
                        variant === 'default' && 'flex-col justify-center gap-2',
                        variant === 'list' && 'justify-start',
                      )}
                    >
                      {!hideIcon && Icon && <Icon className={cn('size-12 shrink-0', iconClassName)} />}
                      <span
                        className={cn(
                          'break-words text-sm font-medium',
                          variant === 'default' && 'text-center',
                          variant === 'list' && 'text-left',
                          textClassName,
                        )}
                      >
                        {buttonLabel}
                      </span>
                    </div>
                  </Button>
                )
              })}
            </div>
          </FormControl>
          {showError && <FormMessage />}
        </FormItem>
      )}
    />
  )
}
